import {
	SiInstagram,
	SiSnapchat,
	SiSpotify,
	SiX,
} from "@icons-pack/react-simple-icons";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
	Calendar,
	ChevronLeft,
	Mail,
	Phone,
	User as UserIcon,
} from "lucide-react";
import { z } from "zod";
import { ErrorComponent } from "~/components/error";
import { PersonalInfo } from "~/components/profile/personal-info";
import { ProfilePicture } from "~/components/profile/profile-picture";
import { SocialInfo } from "~/components/profile/social-info";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { userApi } from "~/lib/api/user";
import { useUserStore } from "~/lib/stores/user";
import { cn } from "~/lib/utils";

export const updateProfileSchema = z.object({
	firstName: z.string().min(1, "First name is required"),
	lastName: z.string().min(1, "Last name is required"),
	email: z.string().email("Invalid email").optional().nullable(),
	dateOfBirth: z.string().optional(),
	country: z.string().optional(),
	phoneNumber: z.string().optional(),
	profilePicture: z.union([z.instanceof(File), z.string()]).optional(),
});

export const Route = createFileRoute("/_protected/u/$profile")({
	component: ProfilePage,
	errorComponent: ErrorComponent,
	loader: async ({ params }) => {
		const response = await userApi.getUserProfile(params.profile);
		if (!response.success) {
			throw new Error(response.message);
		}
		return {
			success: true,
			data: response.data,
		};
	},
});

const socialIconMappings = {
	instagram: SiInstagram,
	twitter: SiX,
	snapchat: SiSnapchat,
	spotify: SiSpotify,
} as const;

export default function ProfilePage() {
	const { data: user } = Route.useLoaderData();
	const { user: currentUser } = useUserStore();
	const router = useRouter();
	const { mutate: updateProfilePicture } = useMutation({
		mutationFn: async (file: File | null) => {
			if (!file) {
				return;
			}
			const response = await userApi.updateProfilePicture(file);
			if (!response.success) {
				throw new Error(response.message);
			}
			return response.data;
		},
		onSuccess: async () => {
			await router.invalidate();
		},
	});

	const _phoneNumber = Number(user.phoneNumber);

	const myProfile = currentUser?.id === user.id;

	return (
		<div className="min-h-screen bg-black text-white">
			{/* Header */}
			<header className="sticky top-0 z-10 bg-black/80 backdrop-blur-xl border-b border-white/10">
				<div className="container max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
					<div className="flex items-center gap-4">
						<Button
							className="inline-flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/5 transition-colors p-0 hover:no-underline"
							variant="link"
							onClick={() => router.history.back()}
						>
							<ChevronLeft className="h-5 w-5" />

							<span className="text-xl font-bold tracking-tight text-white">
								Profile
							</span>
						</Button>
					</div>
				</div>
			</header>

			{/* Main Content */}
			<main className="container max-w-5xl mx-auto px-4 py-8">
				<div className="space-y-6">
					{/* Profile Card */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4 }}
					>
						<Card
							className={cn(
								"bg-white/5 overflow-hidden rounded-xl border-white/10 p-6 sm:p-8 flex gap-6",
								myProfile
									? "flex-col sm:flex-row items-center sm:items-start"
									: "flex-col items-center",
							)}
						>
							{/* Profile Picture */}
							<ProfilePicture
								onChange={updateProfilePicture}
								profilePictureUrl={user.details?.profilePicture ?? undefined}
								className="size-28 md:size-36"
								disabled={!myProfile}
							/>
							{/* User Info */}
							<div
								className={cn(
									"flex-1",
									myProfile ? "text-center sm:text-left" : "text-center",
								)}
							>
								<h2 className="text-2xl sm:text-3xl font-bold text-white">
									{user.details?.firstName} {user.details?.lastName}
								</h2>

								{myProfile ? (
									<div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-white/70">
										<div className="flex items-center justify-center sm:justify-start gap-1.5">
											<Mail className="w-3.5 h-3.5 text-accent/80" />
											<span className="text-sm">
												{user.email || "No email"}
											</span>
										</div>
										{!Number.isNaN(_phoneNumber) ? (
											<div className="flex items-center justify-center sm:justify-start gap-1.5">
												<Phone className="w-3.5 h-3.5 text-accent/80" />
												<span className="text-sm">{user.phoneNumber}</span>
											</div>
										) : null}
									</div>
								) : null}

								{/* Stats */}
								<div className="mt-4 flex flex-wrap items-center justify-center sm:justify-start gap-2">
									<div className="px-3 py-1.5 bg-accent/10 border border-accent/30 rounded-full text-xs font-medium text-accent flex items-center gap-1">
										<Calendar className="w-3 h-3" />
										{user.stats.eventsHosted} Events Hosted
									</div>
									<div className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs font-medium text-white/80 flex items-center gap-1">
										<UserIcon className="w-3 h-3" />
										{user.stats.eventsAttended} Events Attended
									</div>
								</div>

								{user.socials.length > 0 ? (
									<div className="mt-4 flex flex-wrap items-center justify-center md:justify-start gap-2">
										{user.socials.map((social) => {
											const Icon =
												socialIconMappings[
													social.platform as keyof typeof socialIconMappings
												];
											return social.link ? (
												<a
													key={social.platform}
													href={social.link}
													className="p-2 bg-white/5 border border-white/10 rounded-xl text-xs font-medium text-white/80 flex items-center gap-1"
												>
													<Icon className="size-3 stroke-accent" />
												</a>
											) : null;
										})}
									</div>
								) : null}
							</div>
						</Card>
					</motion.div>

					{/* Info Card */}
					{myProfile ? (
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.4, delay: 0.1 }}
							className="grid grid-cols-1 lg:grid-cols-2 gap-6"
						>
							<PersonalInfo
								firstName={user.details?.firstName}
								lastName={user.details?.lastName}
								email={user.email ?? ""}
								phoneNumber={user.phoneNumber}
								username={user.username}
							/>
							<SocialInfo socials={user.socials} />
						</motion.div>
					) : null}
				</div>
			</main>
		</div>
	);
}
